import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import capitalize from 'lodash/capitalize';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import CreditcardFilledIcon from '@atlaskit/icon/glyph/creditcard-filled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { GradientCTAInvertedButton } from '@confluence/change-edition/entry-points/GradientCTAButton';
import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from '@confluence/premium-onboarding/entry-points/useIsPremiumOnboardingFeatureDiscoveryEnabled';
import { SPAViewContext } from '@confluence/spa-view-context';
import {
	useShouldCheckEligibilityForSpotlight,
	getIsPremiumOnboardingHappened,
} from '@confluence/premium-onboarding/entry-points/useShouldCheckEligibilityForSpotlight';
import { BorderNudge } from '@confluence/onboarding-hover-nudge/entry-points/BorderNudge';
import { HOVER_TARGET } from '@confluence/onboarding-hover-nudge/entry-points/constants/HoverTarget';

import { getPillType } from './getPillType';

const i18n = defineMessages({
	daysLeft: {
		id: 'trial-components.action.menu.pill',
		defaultMessage: '{count, plural, =0 {DAYS LEFT} one {# DAY LEFT} other {# DAYS LEFT}}',
		description:
			'The message displayed on the pill informing users how many days are left on their trial',
	},
	addPayment: {
		id: 'trial-components.action.menu.payment-details',
		defaultMessage: 'ADD PAYMENT DETAILS',
		description: 'The text displayed on the pill informing users they need to add payment details',
	},
	creditCardIconLabel: {
		id: 'trial-components.action.menu.label.credit-card',
		defaultMessage: 'Credit Card Icon',
		description: 'Icon Label for Credit Card Icon',
	},
	premiumIconLabel: {
		id: 'trial-components.action.menu.label.premium',
		defaultMessage: 'Premium Icon',
		description: 'Icon Label for Premium Icon',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillContainerInNavigation = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	marginRight: `${token('space.150', '12px')} !important`,
});

export const ActionMenuPill = ({
	daysLeft,
	isOpen,
	setIsOpen,
	triggerProps,
	isPreDunningPeriod,
	hasBillingInformation,
	canAddBillingForTrial,
	canAddBillingForTrialAnalyticsAttributes,
}) => {
	const { edition } = useSessionData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	const { isPremiumOnboardingFeatureDiscoveryEnabled, isPTORTEnabled } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const pillType = getPillType({ isSiteAdmin, hasBillingInformation });
	const [{ isSpotlightCheckEnabled }, { enableSpotlightCheck }] =
		useShouldCheckEligibilityForSpotlight();

	useEffect(() => {
		async function ptoPhaseTwoSpotlightCheck() {
			if (
				isSpotlightCheckEnabled === false &&
				getIsPremiumOnboardingHappened() &&
				isPremiumOnboardingFeatureDiscoveryEnabled
			) {
				await enableSpotlightCheck();
			}
		}
		void ptoPhaseTwoSpotlightCheck();
	}, [isSpotlightCheckEnabled, enableSpotlightCheck, isPremiumOnboardingFeatureDiscoveryEnabled]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: isPreDunningPeriod ? 'addPaymentDetailsPillScreen' : 'daysLeftPillScreen',
				attributes: {
					edition,
					...(canAddBillingForTrial && canAddBillingForTrialAnalyticsAttributes),
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		edition,
		canAddBillingForTrial,
		isPreDunningPeriod,
		canAddBillingForTrialAnalyticsAttributes,
	]);

	const onClick = () => {
		const attributes = {
			triggeredFrom: isPreDunningPeriod ? 'ADD PAYMENT DETAILS' : 'DAYS LEFT',
			...(isPremiumOnboardingFeatureDiscoveryEnabled && {
				pillType,
				trialDaysLeft: daysLeft,
				isPTORTEnabled,
			}),
			...(canAddBillingForTrial && canAddBillingForTrialAnalyticsAttributes),
		};
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: isPremium ? 'premTrialActionScreen' : 'stanTrialActionScreen',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: isPremium ? 'premTrialActionComponent' : 'stanTrialActionComponent',
				attributes,
			},
		}).fire();
		setIsOpen(!isOpen);
	};

	const Icon = isPreDunningPeriod ? CreditcardFilledIcon : PremiumIcon;
	if (!(edition === ConfluenceEdition.PREMIUM || edition === ConfluenceEdition.STANDARD)) {
		return null;
	}

	const icon = (
		<Icon
			label={
				isPreDunningPeriod
					? formatMessage(i18n.creditCardIconLabel)
					: formatMessage(i18n.premiumIconLabel)
			}
			size="small"
		/>
	);

	const content = isPreDunningPeriod
		? formatMessage(i18n.addPayment)
		: formatMessage(i18n.daysLeft, { count: daysLeft });

	return (
		<PillContainerInNavigation>
			<BorderNudge
				target={HOVER_TARGET.PREMIUM_TRIAL}
				customStyles={{
					width: '100%',
					borderRadius: token('border.radius.200', '8px'),
				}}
			>
				<GradientCTAInvertedButton iconBefore={icon} onClick={onClick} {...triggerProps}>
					{capitalize(content)}
				</GradientCTAInvertedButton>
			</BorderNudge>
		</PillContainerInNavigation>
	);
};
